<template>
  <div>
    <v-card-title class="flex-nowrap justify-space-between">
      <span class="text-break">Merchanto Events API V2 documentation</span>
    </v-card-title>

    <!-- OVERVIEW -->
    <v-card-title id="overview">Overview</v-card-title>
    <v-card-text>
      <p>The Merchanto Events API V2 enables merchants and resellers (collectively referred to as "Clients") to integrate with Visa RDR, Visa CDRN, and Mastercard Alerts services. This integration helps prevent disputes and improve operational efficiency in your business.</p>
      <p>This documentation covers:</p>
      <ul class="mt-2">
        <li><b>Event Management:</b> Receive and process real-time event notifications via webhooks</li>
        <li><b>Event Retrieval:</b> Query events programmatically using REST endpoints</li>
        <li><b>Status Updates:</b> Update event outcomes and refund states</li>
      </ul>
    </v-card-text>

    <!-- API WORKFLOW -->
    <v-card-title id="api-workflow">API Workflow</v-card-title>
    <v-card-text>
      <img :src="require('@/assets/images/docs/chargeback/merchanto-api-v2.svg')"
           class="merchanto-api-v2-scheme"
           alt="merchanto-api-v2" />
      <p class="mt-4">The Merchanto API operates through two primary communication patterns:</p>
      <p class="mt-2"><b>Merchanto → Client (Webhooks)</b></p>
      <ol class="mt-2">
        <li><b>Push Event Data:</b> Merchanto sends a POST request to your endpoint when a new event is received from Visa or Mastercard</li>
        <li><b>Status Update Notification:</b> Merchanto sends a PATCH request when an event status changes (either manually in the Merchanto UI or automatically by the platform, such as expiration or automated refund processing)</li>
      </ol>

      <p class="mt-2"><b>Client → Merchanto (API Calls)</b></p>
      <ol class="mt-2">
        <li><b>Update Event Status:</b> You send a PATCH request to update the event outcome and refund state</li>
        <li><b>Retrieve All Events:</b> You send a GET request to fetch a paginated list of events</li>
        <li><b>Retrieve Event by ID:</b> You send a GET request to fetch details for a specific event</li>
      </ol>

      <p class="mt-2"><b>Complete Flow</b></p>
      <ol class="mt-2">
        <li>Merchanto receives an event from Visa/Mastercard and saves it to the platform</li>
        <li>Merchanto sends a POST request to your endpoint with event details</li>
        <li>Your system responds with an HTTP 200 status</li>
        <li>Your system processes the event and sends a PATCH request to Merchanto with the outcome</li>
        <li>Merchanto responds with an HTTP 200 status</li>
        <li>Optionally, your system can query events at any time using GET requests</li>
        <li>If the event status changes in Merchanto (manually or automatically), Merchanto sends a PATCH notification to your endpoint</li>
        <li>Your system responds with an HTTP 200 status</li>
      </ol>
    </v-card-text>

    <!-- 1. GETTING STARTED -->
    <v-card-title id="getting-started">1. Getting Started</v-card-title>

    <v-card-title id="authentication" class="subtitle-1">1.1. Authentication</v-card-title>
    <v-card-text>
      <p>All API requests require Basic Authentication using the same set of credentials for both directions:</p>
      <p class="mt-2"><b>Credential Generation:</b> When your manager creates an account for you on the Merchanto platform, API credentials (username and password) are automatically generated and sent to your email.</p>
      <p class="mt-2"><b>Using Your Credentials:</b></p>
      <ul class="mt-2">
        <li><b>For webhook endpoints (Merchanto → You):</b> You must implement Basic Authentication on your endpoints using these credentials. Merchanto will include these credentials in all webhook requests to your endpoints.</li>
        <li><b>For API calls (You → Merchanto):</b> Use the same credentials when making requests to Merchanto endpoints (retrieving events, updating status, etc.).</li>
      </ul>
      <p class="mt-2"><b>Authentication Header Format:</b></p>
      <pre class="output"><code>Authorization: Basic base64(username:password)</code></pre>
    </v-card-text>

    <v-card-title id="environments" class="subtitle-1">1.2. Environments</v-card-title>
    <v-card-text>
      Merchanto provides two environments for integration:
      <v-simple-table class="merchanto-endpoints-table my-2">
        <template #default>
          <thead>
          <tr>
            <th class="text-left">Environment</th>
            <th class="text-left">Base URL</th>
            <th class="text-left">Purpose</th>
          </tr>
          </thead>
          <tbody>
          <tr>
            <td><b>Sandbox</b></td>
            <td>https://dev-api.merchanto.org</td>
            <td>Development and testing</td>
          </tr>
          <tr>
            <td><b>Production</b></td>
            <td>https://api.merchanto.org</td>
            <td>Live production environment</td>
          </tr>
          </tbody>
        </template>
      </v-simple-table>
      <p class="mt-2"><b>Note:</b> All endpoints are documented in our <a href="https://dev-api.merchanto.org/api#/" target="_blank">Swagger API Reference</a></p>
    </v-card-text>

    <v-card-title id="client-endpoints" class="subtitle-1">1.3. Client Endpoints</v-card-title>
    <v-card-text>
      You must configure and provide two endpoints secured with Basic Authentication:
      <v-simple-table class="request-type-table my-2">
        <template #default>
          <thead>
          <tr>
            <th class="text-left">Purpose</th>
            <th class="text-left">Method</th>
            <th class="text-left">Endpoint Pattern</th>
          </tr>
          </thead>
          <tbody>
          <tr>
            <td>Receive new events</td>
            <td><b>POST</b></td>
            <td>https://your-domain.com/api/consume-event</td>
          </tr>
          <tr>
            <td>Receive status updates</td>
            <td><b>PATCH</b></td>
            <td>https://your-domain.com/api/consume-event/{eventId}/status</td>
          </tr>
          </tbody>
        </template>
      </v-simple-table>
      <p class="mt-2">Replace <code>your-domain.com</code> with your actual domain and adjust the paths according to your infrastructure.</p>
    </v-card-text>

    <v-card-title id="integration-testing" class="subtitle-1">1.4. Integration Testing</v-card-title>
    <v-card-text>
      <p>Before moving to production, you must configure and test your integration in the Merchanto Sandbox Environment. The sandbox system supports:</p>
      <ul class="mt-2">
        <li>Triggering webhook requests to your endpoints</li>
        <li>Simulating all event types and scenarios</li>
        <li>Validating response handling</li>
      </ul>
      <p class="mt-2">Contact your platform representative to schedule testing sessions.</p>
    </v-card-text>

    <v-card-title id="http-status-codes" class="subtitle-1">1.5. HTTP Status Codes</v-card-title>
    <v-card-text>
      <p>Standard HTTP status codes used across all endpoints:</p>
      <v-simple-table class="status-code-table my-2">
        <template #default>
          <thead>
            <tr>
              <th class="text-left">Code</th>
              <th class="text-left">Name</th>
              <th class="text-left">Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td><b>200</b></td>
              <td>OK</td>
              <td>Request processed successfully</td>
            </tr>
            <tr>
              <td><b>400</b></td>
              <td>Bad Request</td>
              <td>Invalid request format or missing required fields</td>
            </tr>
            <tr>
              <td><b>401</b></td>
              <td>Unauthorized</td>
              <td>Authentication credentials missing or invalid</td>
            </tr>
            <tr>
              <td><b>404</b></td>
              <td>Not Found</td>
              <td>Requested resource does not exist</td>
            </tr>
            <tr>
              <td><b>500</b></td>
              <td>Internal Server Error</td>
              <td>Unexpected server error occurred</td>
            </tr>
          </tbody>
        </template>
      </v-simple-table>
    </v-card-text>

    <v-card-title id="event-statuses" class="subtitle-1">1.6. Event Statuses</v-card-title>
    <v-card-text>
      <p>When processing events, you must provide both an <b>Event Status</b> and a <b>Refund State</b>.</p>
      <p class="mt-2"><b>Event Status Values</b></p>
      <v-simple-table class="event-status-desc-table my-2">
        <template #default>
          <thead>
            <tr>
              <th class="text-left">Status Label</th>
              <th class="text-left">API Value</th>
              <th class="text-left">When to Use</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td><b>Pending</b></td>
              <td>pending</td>
              <td>Event is new and awaiting action</td>
            </tr>
            <tr>
              <td><b>Resolved</b></td>
              <td>resolved</td>
              <td>Order canceled, credit processed, or customer issue resolved</td>
            </tr>
            <tr>
              <td><b>Declined</b></td>
              <td>declined</td>
              <td>Order shipped or service delivered; dispute will be contested</td>
            </tr>
            <tr>
              <td><b>Deleted</b></td>
              <td>deleted</td>
              <td>Event removed manually or automatically</td>
            </tr>
            <tr>
              <td><b>Previously Resolved</b></td>
              <td>previously_resolved</td>
              <td>Transaction was already refunded or canceled before this event</td>
            </tr>
            <tr>
              <td><b>Not Found</b></td>
              <td>notfound</td>
              <td>Transaction not found in your system</td>
            </tr>
            <tr>
              <td><b>Expired</b></td>
              <td>expired</td>
              <td>Event deadline passed; too late to take action</td>
            </tr>
          </tbody>
        </template>
      </v-simple-table>
      <p class="mt-2"><b>Refund State Values</b></p>
      <v-simple-table class="event-refund-status-desc-table my-2">
        <template #default>
          <thead>
            <tr>
              <th class="text-left">State Label</th>
              <th class="text-left">API Value</th>
              <th class="text-left">When to Use</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td><b>Refunded</b></td>
              <td>refunded</td>
              <td>Transaction has been refunded</td>
            </tr>
            <tr>
              <td><b>Not Refunded</b></td>
              <td>not refunded</td>
              <td>Transaction has not been refunded</td>
            </tr>
            <tr>
              <td><b>Not Settled</b></td>
              <td>not settled</td>
              <td>Transaction was not settled (authorization only)</td>
            </tr>
          </tbody>
        </template>
      </v-simple-table>
    </v-card-text>

    <!-- 2. RECEIVING EVENTS (WEBHOOKS) -->
    <v-card-title id="receiving-events">2. Receiving Events (Webhooks)</v-card-title>
    <v-card-text>
      Merchanto sends webhook notifications to your endpoints when events occur. You must implement handlers for these requests.
    </v-card-text>

    <v-card-title id="push-event-data" class="subtitle-1">2.1. Push Event Data</v-card-title>
    <v-card-text>
      <p>When Merchanto receives a new event from Visa or Mastercard, we send the event details to your endpoint in near real-time.</p>
      <p class="mt-2"><b>Endpoint Configuration:</b></p>
      <ul class="mt-2">
        <li><b>URL:</b> Your POST endpoint (e.g., <code>https://your-domain.com/api/consume-event</code>)</li>
        <li><b>Method:</b> POST</li>
        <li><b>Authentication:</b> Basic Auth (provided credentials by Merchanto)</li>
        <li><b>Content-Type:</b> application/json</li>
      </ul>
      <p class="mt-2"><b>Field Reference:</b> see <a @click="scrollToElement('detailed-object-schemas')">5. Detailed Object Schemas</a></p>
      <p class="mt-2"><b>Request Example:</b></p>
      <p class="mt-2"><i>Authentication: Basic Auth with provided credentials by Merchanto</i></p>
      <pre class="output"><code>
{
  "eventId": "9336e3a0",
  "eventTimestamp": "2024-02-14T01:25:36.000Z",
  "age": "12",
  "issuer": "Citi US",
  "cardNumber": "542418******0066",
  "cardBin": "542418",
  "cardLast": "0066",
  "transactionDate": "2024-03-27T07:32:58.000Z",
  "merchantDescriptor": "Regional Market",
  "amount": 8.69,
  "currency": "EUR",
  "authCode": "327027",
  "arn": "82644314064000003717640",
  "transactionId": "MNWH7N30I",
  "chargebackReasonCode": "4841",
  "chargebackAmount": 8.69,
  "chargebackCurrency": "EUR",
  "cardType": "masterCard",
  "service": "Mastercard Alert",
  "status": "pending",
  "caid": "123456789012345",
  "bin": "826443",
  "stripeChargeId": "ch_3N8Yj2JkLmNoPq",
  "comment": null,
  "accountName": "merchant_account_01",
  "eventCost": "32.00",
  "expiredIn": "24 hours",
  "companyId": 1536,
  "website": {
    "id": 256,
    "name": "MyBestSite",
    "mcc": 8299,
    "status": "active"
  }
}
      </code></pre>
      <p class="mt-2"><b>Expected Response:</b></p>
      <p class="mt-2">Your endpoint must respond with <b>HTTP 200 OK</b> to acknowledge receipt. Any non-2xx response will trigger retry logic (see section 2.3).</p>
    </v-card-text>

    <v-card-title id="status-update-notification" class="subtitle-1">2.2. Status Update Notification</v-card-title>
    <v-card-text>
      <p>When an event status changes in the Merchanto UI (manually or automatically), we send a notification to your endpoint.</p>
      <p class="mt-2"><b>Endpoint Configuration:</b></p>
      <ul class="mt-2">
        <li><b>URL:</b> Your PATCH endpoint (e.g., <code>https://your-domain.com/api/consume-event/{eventId}/status</code>)</li>
        <li><b>Method:</b> PATCH</li>
        <li><b>Authentication:</b> Basic Auth (provided credentials by Merchanto)</li>
        <li><b>Content-Type:</b> application/json</li>
      </ul>
      <p class="mt-2"><b>Request Payload Structure:</b></p>
      <v-simple-table class="response-example-table">
        <template #default>
          <thead>
          <tr>
            <th class="text-left">Field</th>
            <th class="text-left">Type</th>
            <th class="text-left">Description</th>
          </tr>
          </thead>
          <tbody>
          <tr>
            <td><b>status*</b></td>
            <td>
              <p><i>enum</i></p>
              <p><b>required:</b>&nbsp;<i>true</i></p>
              <p><b>nullable:</b>&nbsp;<i>false</i></p>
            </td>
            <td>
              New event status. Possible values:
              <ul>
                <li>pending</li>
                <li>resolved</li>
                <li>declined</li>
                <li>deleted</li>
                <li>previously_resolved</li>
                <li>notfound</li>
                <li>expired</li>
              </ul>
            </td>
          </tr>
          <tr>
            <td><b>refunded*</b></td>
            <td>
              <p><i>enum</i></p>
              <p><b>required:</b>&nbsp;<i>true</i></p>
              <p><b>nullable:</b>&nbsp;<i>false</i></p>
            </td>
            <td>
              Refund state. Possible values:
              <ul>
                <li>refunded</li>
                <li>not refunded</li>
                <li>not settled</li>
              </ul>
            </td>
          </tr>
          </tbody>
        </template>
      </v-simple-table>
      <p class="mt-2"><b>Request Examples:</b></p>
      <p class="mt-2"><i>Authentication: Basic Auth with provided credentials by Merchanto</i></p>
      <p class="mt-2">Example 1: Status with refund state</p>
      <pre class="output"><code>
{
  "status": "resolved",
  "refunded": "refunded"
}
      </code></pre>
      <p class="mt-2">Example 2: Status only (expired event)</p>
      <pre class="output"><code>
{
  "status": "expired"
}
      </code></pre>
      <p class="mt-2"><b>Expected Response:</b></p>
      <p class="mt-2">Your endpoint must respond with <b>HTTP 200 OK</b> to acknowledge receipt.</p>
    </v-card-text>

    <v-card-title id="retry-logic" class="subtitle-1">2.3. Retry Logic</v-card-title>
    <v-card-text>
      <p>If your endpoint does not respond with a 2xx HTTP status code, Merchanto will automatically retry the request:</p>
      <ul class="mt-2">
        <li><b>Total attempts:</b> 5 (1 initial + 4 retries)</li>
        <li><b>Retry intervals:</b>
          <ul>
            <li>1st retry: 1 minute after initial failure</li>
            <li>2nd retry: 10 minutes after 1st retry</li>
            <li>3rd retry: 1 hour after 2nd retry</li>
            <li>4th retry: 3 hours after 3rd retry</li>
          </ul>
        </li>
      </ul>
      <p class="mt-2">Ensure your webhook handlers are idempotent to safely handle potential duplicate deliveries.</p>
    </v-card-text>

    <!-- 3. UPDATING EVENT STATUS -->
    <v-card-title id="updating-event-status">3. Updating Event Status</v-card-title>
    <v-card-text>
      After processing an event, you must inform Merchanto of the outcome by updating the event status.
    </v-card-text>

    <v-card-title id="endpoint-details" class="subtitle-1">3.1. Endpoint Details</v-card-title>
    <v-card-text>
      <ul class="mt-2">
        <li><b>URL:</b> <code>https://api.merchanto.org/api/merchant-api/v2/{eventId}/status</code></li>
        <li><b>Sandbox URL:</b> <code>https://dev-api.merchanto.org/api/merchant-api/v2/{eventId}/status</code></li>
        <li><b>Method:</b> PATCH</li>
        <li><b>Authentication:</b> Basic Auth (credentials provided by Merchanto)</li>
        <li><b>Content-Type:</b> application/json</li>
        <li><b>Swagger:</b> <a href="https://dev-api.merchanto.org/api#/default/ChargebackMerchantApiV2Controller_patchChargebackEventStatus" target="_blank">API Reference</a></li>
      </ul>
      <p class="mt-2">Replace <code>{eventId}</code> with the actual event ID from the webhook payload.</p>
    </v-card-text>

    <v-card-title id="request-payload" class="subtitle-1">3.2. Request Payload</v-card-title>
    <v-card-text>
      <v-simple-table class="response-example-table">
        <template #default>
          <thead>
            <tr>
              <th class="text-left">Field</th>
              <th class="text-left">Type</th>
              <th class="text-left">Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td><b>status*</b></td>
              <td>
                <p><i>enum</i></p>
                <p><b>required:</b>&nbsp;<i>true</i></p>
                <p><b>nullable:</b>&nbsp;<i>false</i></p>
              </td>
              <td>
                Event outcome. Possible values:
                <ul>
                  <li>resolved</li>
                  <li>declined</li>
                  <li>previously_resolved</li>
                  <li>notfound</li>
                </ul>
              </td>
            </tr>
            <tr>
              <td><b>refunded*</b></td>
              <td>
                <p><i>enum</i></p>
                <p><b>required:</b>&nbsp;<i>true</i></p>
                <p><b>nullable:</b>&nbsp;<i>false</i></p>
              </td>
              <td>
                Refund state. Possible values:
                <ul>
                  <li>refunded</li>
                  <li>not refunded</li>
                  <li>not settled</li>
                </ul>
              </td>
            </tr>
          </tbody>
        </template>
      </v-simple-table>
      <p class="mt-2"><i>Authentication: Basic Auth with credentials provided by Merchanto</i></p>
      <p class="mt-2"><b>Request Body Example:</b></p>
      <pre class="output"><code>
{
  "status": "resolved",
  "refunded": "refunded"
}
        </code></pre>
    </v-card-text>

    <v-card-title id="response-examples" class="subtitle-1">3.3. Response Examples</v-card-title>
    <v-card-text>
      <p><b>Success Response (200 OK)</b></p>
      <p class="mt-2">When the update is processed successfully:</p>
      <pre class="output"><code>
{
  "status": "resolved",
  "refunded": "refunded"
}
        </code></pre>
      <p class="mt-2">If the event has expired or been deleted:</p>
      <pre class="output"><code>
{
  "status": "expired"
}
        </code></pre>
      <p class="mt-2"><b>Error Response (400 Bad Request)</b></p>
      <p class="mt-2">When required fields are missing or invalid:</p>
      <pre class="output"><code>
{
  "errors": [
    {
      "field": "status",
      "message": "status is required and cannot be empty"
    }
  ]
}
        </code></pre>
      <p class="mt-2"><b>Error Response (401 Unauthorized)</b></p>
      <p class="mt-2">Authentication credentials are missing or invalid. Verify your credentials and try again.</p>
      <p class="mt-2"><b>Error Response (500 Internal Server Error)</b></p>
      <p class="mt-2">An unexpected server error occurred. If the problem persists, contact Merchanto support.</p>
    </v-card-text>

    <!-- 4. RETRIEVING EVENTS -->
    <v-card-title id="retrieving-events">4. Retrieving Events</v-card-title>
    <v-card-text>
      You can query events programmatically using REST endpoints to retrieve event data on demand.
    </v-card-text>

    <v-card-title id="get-all-events" class="subtitle-1">4.1. Get All Events</v-card-title>
    <v-card-text>
      <p>Retrieve a paginated list of all events with optional date filtering.</p>
      <p class="mt-2"><b>Endpoint Details:</b></p>
      <ul class="mt-2">
        <li><b>URL:</b> <code>https://api.merchanto.org/api/merchant-api/v2/events</code></li>
        <li><b>Sandbox URL:</b> <code>https://dev-api.merchanto.org/api/merchant-api/v2/events</code></li>
        <li><b>Method:</b> GET</li>
        <li><b>Authentication:</b> Basic Auth (credentials provided by Merchanto)</li>
        <li><b>Swagger:</b> <a href="https://dev-api.merchanto.org/api#/" target="_blank">API Reference</a></li>
      </ul>
      <p class="mt-2"><b>Query Parameters:</b></p>
      <v-simple-table class="response-example-table">
        <template #default>
          <thead>
            <tr>
              <th class="text-left">Parameter</th>
              <th class="text-left">Type</th>
              <th class="text-left">Default</th>
              <th class="text-left">Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td><b>page*</b></td>
              <td>
                <p><i>number</i></p>
                <p><b>required:</b>&nbsp;<i>true</i></p>
                <p><b>nullable:</b>&nbsp;<i>false</i></p>
              </td>
              <td>1</td>
              <td>Page number (starting from 1)</td>
            </tr>
            <tr>
              <td><b>itemsPerPage*</b></td>
              <td>
                <p><i>number</i></p>
                <p><b>required:</b>&nbsp;<i>true</i></p>
                <p><b>nullable:</b>&nbsp;<i>false</i></p>
              </td>
              <td>50</td>
              <td>Events per page. Maximum allowed value is 500 (values above 500 will be automatically capped)</td>
            </tr>
            <tr>
              <td><b>chargebackEventDate</b></td>
              <td>
                <p><i>string</i></p>
                <p><b>required:</b>&nbsp;<i>false</i></p>
                <p><b>nullable:</b>&nbsp;<i>false</i></p>
              </td>
              <td>-</td>
              <td>Date range filter in format yyyy-MM-dd,yyyy-MM-dd (e.g., 2023-07-25,2023-07-26)</td>
            </tr>
          </tbody>
        </template>
      </v-simple-table>
      <p class="mt-2"><b>Important:</b> If either <code>page</code> or <code>itemsPerPage</code> is not provided in the request, both parameters will be set to their default values (page=1, itemsPerPage=50).</p>
      <p class="mt-2"><b>Request Example:</b></p>
      <p class="mt-2"><i>Authentication: Basic Auth with credentials provided by Merchanto</i></p>
      <pre class="output"><code>GET /api/merchant-api/v2/events?page=1&itemsPerPage=50</code></pre>
      <p class="mt-2">With date filter:</p>
      <pre class="output"><code>GET /api/merchant-api/v2/events?page=1&itemsPerPage=50&chargebackEventDate=2023-07-25,2023-07-26</code></pre>
      <p class="mt-2"><b>Response Structure:</b></p>
      <v-simple-table class="response-example-table">
        <template #default>
          <thead>
            <tr>
              <th class="text-left">Field</th>
              <th class="text-left">Type</th>
              <th class="text-left">Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td><b>data*</b></td>
              <td>
                <p><i>array</i></p>
                <p><b>required:</b>&nbsp;<i>true</i></p>
                <p><b>nullable:</b>&nbsp;<i>false</i></p>
              </td>
              <td>Array of event objects (see section 4.3)</td>
            </tr>
            <tr>
              <td><b>totalCount*</b></td>
              <td>
                <p><i>number</i></p>
                <p><b>required:</b>&nbsp;<i>true</i></p>
                <p><b>nullable:</b>&nbsp;<i>false</i></p>
              </td>
              <td>Total number of events matching the filter criteria</td>
            </tr>
          </tbody>
        </template>
      </v-simple-table>
      <p class="mt-2"><b>Response Example:</b></p>
      <pre class="output"><code>
{
  "data": [
    {
      "eventId": "9336e3a0",
      "eventTimestamp": "2024-02-14T01:25:36.000Z",
      "cardNumber": "542418******0066",
      "amount": 8.69,
      "currency": "EUR",
      "status": "resolved",
      ...
    }
  ],
  "totalCount": 1250
}
        </code></pre>
      <p class="mt-2"><b>Response Codes:</b></p>
      <v-simple-table class="response-codes-table my-2">
        <template #default>
          <thead>
            <tr>
              <th class="text-left">Code</th>
              <th class="text-left">Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td><b>200</b></td>
              <td>Success - returns event list and total count</td>
            </tr>
            <tr>
              <td><b>401</b></td>
              <td>Authentication failed</td>
            </tr>
            <tr>
              <td><b>500</b></td>
              <td>Internal server error</td>
            </tr>
          </tbody>
        </template>
      </v-simple-table>
    </v-card-text>

    <v-card-title id="get-event-by-eventid" class="subtitle-1">4.2. Get Event by eventId</v-card-title>
    <v-card-text>
      <p>Retrieve detailed information for a specific event using its <code>eventId</code>.</p>
      <p class="mt-2"><b>Endpoint Details:</b></p>
      <ul class="mt-2">
        <li><b>URL:</b> <code>https://api.merchanto.org/api/merchant-api/v2/events/{eventId}/details</code></li>
        <li><b>Sandbox URL:</b> <code>https://dev-api.merchanto.org/api/merchant-api/v2/events/{eventId}/details</code></li>
        <li><b>Method:</b> GET</li>
        <li><b>Authentication:</b> Basic Auth (credentials automatically generated by Merchanto)</li>
        <li><b>Swagger:</b> <a href="https://dev-api.merchanto.org/api#/" target="_blank">API Reference</a></li>
      </ul>
      <p class="mt-2">Replace <code>{eventId}</code> with the actual event ID from the webhook payload.</p>
      <p class="mt-2"><b>Request Example:</b></p>
      <p class="mt-2"><i>Authentication: Basic Auth with credentials provided by Merchanto</i></p>
      <pre class="output"><code>GET /api/merchant-api/v2/events/9336e3a0/details</code></pre>
      <p class="mt-2"><b>Response:</b></p>
      <p class="mt-2">Returns a single event object with full details (see section <a @click="scrollToElement('detailed-object-schemas')">5. Detailed Object Schemas</a> for complete structure).</p>
      <p class="mt-2"><b>Response Codes:</b></p>
      <v-simple-table class="response-codes-table my-2">
        <template #default>
          <thead>
            <tr>
              <th class="text-left">Code</th>
              <th class="text-left">Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td><b>200</b></td>
              <td>Success - returns event details</td>
            </tr>
            <tr>
              <td><b>401</b></td>
              <td>Authentication failed</td>
            </tr>
            <tr>
              <td><b>404</b></td>
              <td>Event not found</td>
            </tr>
            <tr>
              <td><b>500</b></td>
              <td>Internal server error</td>
            </tr>
          </tbody>
        </template>
      </v-simple-table>
    </v-card-text>

    <v-card-title id="detailed-object-schemas" >5. Detailed Object Schemas</v-card-title>
    <v-card-title id="event-object" class="subtitle-1">5.1. Event Object</v-card-title>
    <v-card-text>
      <v-simple-table class="response-example-table">
        <template #default>
          <thead>
          <tr>
            <th class="text-left">Field</th>
            <th class="text-left">Type</th>
            <th class="text-left">Max Length</th>
            <th class="text-left">Description</th>
          </tr>
          </thead>
          <tbody>
          <tr>
            <td><b>eventId*</b></td>
            <td>
              <p><i>string</i></p>
              <p><b>required:</b>&nbsp;<i>true</i></p>
              <p><b>nullable:</b>&nbsp;<i>false</i></p>
            </td>
            <td>60</td>
            <td>Unique identifier for this event</td>
          </tr>
          <tr>
            <td><b>eventTimestamp*</b></td>
            <td>
              <p><i>string (ISO 8601)</i></p>
              <p><b>required:</b>&nbsp;<i>true</i></p>
              <p><b>nullable:</b>&nbsp;<i>true</i></p>
            </td>
            <td>-</td>
            <td>Date and time when the event occurred</td>
          </tr>
          <tr>
            <td><b>createdAt*</b></td>
            <td>
              <p><i>string (ISO 8601)</i></p>
              <p><b>required:</b>&nbsp;<i>true</i></p>
              <p><b>nullable:</b>&nbsp;<i>false</i></p>
            </td>
            <td>-</td>
            <td>When the event was created in Merchanto</td>
          </tr>
          <tr>
            <td><b>age*</b></td>
            <td>
              <p><i>string</i></p>
              <p><b>required:</b>&nbsp;<i>true</i></p>
              <p><b>nullable:</b>&nbsp;<i>true</i></p>
            </td>
            <td>50</td>
            <td>Hours elapsed between transaction and event</td>
          </tr>
          <tr>
            <td><b>issuer*</b></td>
            <td>
              <p><i>string</i></p>
              <p><b>required:</b>&nbsp;<i>true</i></p>
              <p><b>nullable:</b>&nbsp;<i>true</i></p>
            </td>
            <td>100</td>
            <td>Card issuing bank name</td>
          </tr>
          <tr>
            <td><b>cardNumber*</b></td>
            <td>
              <p><i>string</i></p>
              <p><b>required:</b>&nbsp;<i>true</i></p>
              <p><b>nullable:</b>&nbsp;<i>false</i></p>
            </td>
            <td>19</td>
            <td>Masked card number (e.g., "542418******0066")</td>
          </tr>
          <tr>
            <td><b>cardBin*</b></td>
            <td>
              <p><i>string</i><br/></p>
              <p><b>required:</b>&nbsp;<i>true</i></p>
              <p><b>nullable:</b>&nbsp;<i>false</i></p>
            </td>
            <td>6</td>
            <td>First 6 digits of card number</td>
          </tr>
          <tr>
            <td><b>cardLast*</b></td>
            <td>
              <p><i>string</i></p>
              <p><b>required:</b>&nbsp;<i>true</i></p>
              <p><b>nullable:</b>&nbsp;<i>false</i></p>
            </td>
            <td>4</td>
            <td>Last 4 digits of card number</td>
          </tr>
          <tr>
            <td><b>transactionDate*</b></td>
            <td>
              <p><i>string (ISO 8601)</i></p>
              <p><b>required:</b>&nbsp;<i>true</i></p>
              <p><b>nullable:</b>&nbsp;<i>false</i></p>
            </td>
            <td>-</td>
            <td>Date and time of original transaction</td>
          </tr>
          <tr>
            <td><b>merchantDescriptor*</b></td>
            <td>
              <p><i>string</i></p>
              <p><b>required:</b>&nbsp;<i>true</i></p>
              <p><b>nullable:</b>&nbsp;<i>true</i></p>
            </td>
            <td>100</td>
            <td>Merchant name as shown on cardholder statement</td>
          </tr>
          <tr>
            <td><b>amount*</b></td>
            <td>
              <p><i>number</i></p>
              <p><b>required:</b>&nbsp;<i>true</i></p>
              <p><b>nullable:</b>&nbsp;<i>false</i></p>
            </td>
            <td>-</td>
            <td>Transaction amount</td>
          </tr>
          <tr>
            <td><b>currency*</b></td>
            <td>
              <p><i>string</i></p>
              <p><b>required:</b>&nbsp;<i>true</i></p>
              <p><b>nullable:</b>&nbsp;<i>false</i></p>
            </td>
            <td>3</td>
            <td>ISO 4217 currency code (e.g., "USD", "EUR")</td>
          </tr>
          <tr>
            <td><b>authCode*</b></td>
            <td>
              <p><i>string</i></p>
              <p><b>required:</b>&nbsp;<i>true</i></p>
              <p><b>nullable:</b>&nbsp;<i>true</i></p>
            </td>
            <td>20</td>
            <td>Transaction authorization code</td>
          </tr>
          <tr>
            <td><b>arn*</b></td>
            <td>
              <p><i>string</i></p>
              <p><b>required:</b>&nbsp;<i>true</i></p>
              <p><b>nullable:</b>&nbsp;<i>true</i></p>
            </td>
            <td>23</td>
            <td>Acquirer Reference Number (23-digit unique identifier)</td>
          </tr>
          <tr>
            <td><b>transactionId*</b></td>
            <td>
              <p><i>string</i></p>
              <p><b>required:</b>&nbsp;<i>true</i></p>
              <p><b>nullable:</b>&nbsp;<i>true</i></p>
            </td>
            <td>64</td>
            <td>Unique transaction reference (Trace ID for Mastercard)</td>
          </tr>
          <tr>
            <td><b>chargebackReasonCode*</b></td>
            <td>
              <p><i>string</i></p>
              <p><b>required:</b>&nbsp;<i>true</i></p>
              <p><b>nullable:</b>&nbsp;<i>true</i></p>
            </td>
            <td>30</td>
            <td>Card network reason code for the dispute</td>
          </tr>
          <tr>
            <td><b>chargebackAmount*</b></td>
            <td>
              <p><i>number</i></p>
              <p><b>required:</b>&nbsp;<i>true</i></p>
              <p><b>nullable:</b>&nbsp;<i>true</i></p>
            </td>
            <td>-</td>
            <td>Amount being disputed</td>
          </tr>
          <tr>
            <td><b>chargebackCurrency*</b></td>
            <td>
              <p><i>string</i></p>
              <p><b>required:</b>&nbsp;<i>true</i></p>
              <p><b>nullable:</b>&nbsp;<i>true</i></p>
            </td>
            <td>3</td>
            <td>Currency of disputed amount</td>
          </tr>
          <tr>
            <td><b>comment*</b></td>
            <td>
              <p><i>string</i></p>
              <p><b>required:</b>&nbsp;<i>true</i></p>
              <p><b>nullable:</b>&nbsp;<i>true</i></p>
            </td>
            <td>255</td>
            <td>Administrator notes or comments</td>
          </tr>
          <tr>
            <td><b>cardType*</b></td>
            <td>
              <p><i>enum</i></p>
              <p><b>required:</b>&nbsp;<i>true</i></p>
              <p><b>nullable:</b>&nbsp;<i>false</i></p>
            </td>
            <td>-</td>
            <td>
              Card network. Possible values:
              <ul>
                <li>masterCard</li>
                <li>visa</li>
                <li>americanExpress</li>
                <li>discover</li>
              </ul>
            </td>
          </tr>
          <tr>
            <td><b>service*</b></td>
            <td>
              <p><i>enum</i></p>
              <p><b>required:</b>&nbsp;<i>true</i></p>
              <p><b>nullable:</b>&nbsp;<i>false</i></p>
            </td>
            <td>-</td>
            <td>
              Alert source. Possible values:
              <ul>
                <li>Mastercard Alert</li>
                <li>Visa RDR</li>
                <li>Visa CDRN</li>
                <li>Visa TC40</li>
              </ul>
            </td>
          </tr>
          <tr>
            <td><b>status*</b></td>
            <td>
              <p><i>enum</i></p>
              <p><b>required:</b>&nbsp;<i>true</i></p>
              <p><b>nullable:</b>&nbsp;<i>false</i></p>
            </td>
            <td>-</td>
            <td>Current status (see Event Statuses section)</td>
          </tr>
          <tr>
            <td><b>caid*</b></td>
            <td>
              <p><i>string</i></p>
              <p><b>required:</b>&nbsp;<i>true</i></p>
              <p><b>nullable:</b>&nbsp;<i>true</i></p>
            </td>
            <td>30</td>
            <td>Card Acceptor ID - unique merchant account identifier</td>
          </tr>
          <tr>
            <td><b>bin*</b></td>
            <td>
              <p><i>string</i></p>
              <p><b>required:</b>&nbsp;<i>true</i></p>
              <p><b>nullable:</b>&nbsp;<i>true</i></p>
            </td>
            <td>30</td>
            <td>Acquirer Bank Identification Number</td>
          </tr>
          <tr>
            <td><b>eventCost*</b></td>
            <td>
              <p><i>string</i></p>
              <p><b>required:</b>&nbsp;<i>true</i></p>
              <p><b>nullable:</b>&nbsp;<i>false</i></p>
            </td>
            <td>-</td>
            <td>Cost charged for processing this event</td>
          </tr>
          <tr>
            <td><b>companyId*</b></td>
            <td>
              <p><i>number</i></p>
              <p><b>required:</b>&nbsp;<i>true</i></p>
              <p><b>nullable:</b>&nbsp;<i>false</i></p>
            </td>
            <td>-</td>
            <td>Your company identifier in Merchanto system</td>
          </tr>
          <tr>
            <td><b>website*</b></td>
            <td>
              <p><i>object</i></p>
              <p><b>required:</b>&nbsp;<i>true</i></p>
              <p><b>nullable:</b>&nbsp;<i>false</i></p>
            </td>
            <td>-</td>
            <td><a @click="scrollToElement('website-object')">Website Object</a></td>
          </tr>
          <tr>
            <td><b>accountName*</b></td>
            <td>
              <p><i>string</i></p>
              <p><b>required:</b>&nbsp;<i>true</i></p>
              <p><b>nullable:</b>&nbsp;<i>false</i></p>
            </td>
            <td>255</td>
            <td>Your merchant account name</td>
          </tr>
          <tr>
            <td><b>expiredIn*</b></td>
            <td>
              <p><i>string</i></p>
              <p><b>required:</b>&nbsp;<i>true</i></p>
              <p><b>nullable:</b>&nbsp;<i>true</i></p>
            </td>
            <td>-</td>
            <td>Time remaining to respond to this event</td>
          </tr>
          <tr>
            <td><b>outcome</b></td>
            <td>
              <p><i>object</i></p>
              <p><b>required:</b>&nbsp;<i>false</i></p>
              <p><b>nullable:</b>&nbsp;<i>false</i></p>
            </td>
            <td>-</td>
            <td><a @click="scrollToElement('outcome-object')">Outcome Object</a>. Present only after event is processed</td>
          </tr>
          <tr>
            <td><b>stripeChargeId</b></td>
            <td>
              <p><i>string</i></p>
              <p><b>required:</b>&nbsp;<i>false</i></p>
              <p><b>nullable:</b>&nbsp;<i>false</i></p>
            </td>
            <td>255</td>
            <td>Stripe charge identifier (if applicable)</td>
          </tr>
          <tr>
            <td><b>adyenChargeId</b></td>
            <td>
              <p><i>string</i></p>
              <p><b>required:</b>&nbsp;<i>false</i></p>
              <p><b>nullable:</b>&nbsp;<i>false</i></p>
            </td>
            <td>255</td>
            <td>Adyen charge identifier (if applicable)</td>
          </tr>
          </tbody>
        </template>
      </v-simple-table>
    </v-card-text>
    <v-card-title id="website-object" class="subtitle-1">5.2. Website Object</v-card-title>
    <v-card-text>
      <v-simple-table class="response-example-table">
        <template #default>
          <thead>
          <tr>
            <th class="text-left">Field</th>
            <th class="text-left">Type</th>
            <th class="text-left">Max Length</th>
            <th class="text-left">Description</th>
          </tr>
          </thead>
          <tbody>
          <tr>
            <td><b>id*</b></td>
            <td>
              <p><i>number</i></p>
              <p><b>required:</b>&nbsp;<i>true</i></p>
              <p><b>nullable:</b>&nbsp;<i>false</i></p>
            </td>
            <td>-</td>
            <td>Unique website identifier</td>
          </tr>
          <tr>
            <td><b>name*</b></td>
            <td>
              <p><i>string</i></p>
              <p><b>required:</b>&nbsp;<i>true</i></p>
              <p><b>nullable:</b>&nbsp;<i>false</i></p>
            </td>
            <td>255</td>
            <td>Website name</td>
          </tr>
          <tr>
            <td><b>mcc*</b></td>
            <td>
              <p><i>number</i></p>
              <p><b>required:</b>&nbsp;<i>true</i></p>
              <p><b>nullable:</b>&nbsp;<i>false</i></p>
            </td>
            <td>50</td>
            <td>Merchant Category Code</td>
          </tr>
          <tr>
            <td><b>status*</b></td>
            <td>
              <p><i>enum</i></p>
              <p><b>required:</b>&nbsp;<i>true</i></p>
              <p><b>nullable:</b>&nbsp;<i>false</i></p>
            </td>
            <td>-</td>
            <td>
              Website status. Possible values:
              <ul>
                <li>active</li>
                <li>inactive</li>
                <li>deleted</li>
              </ul>
            </td>
          </tr>
          </tbody>
        </template>
      </v-simple-table>
    </v-card-text>
    <v-card-title id="outcome-object" class="subtitle-1">5.3. Outcome Object (optional - present only after event is processed)</v-card-title>
    <v-card-text>
      <v-simple-table class="response-example-table">
        <template #default>
          <thead>
            <tr>
              <th class="text-left">Field</th>
              <th class="text-left">Type</th>
              <th class="text-left">Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td><b>id*</b></td>
              <td>
                <p><i>number</i></p>
                <p><b>required:</b>&nbsp;<i>true</i></p>
                <p><b>nullable:</b>&nbsp;<i>false</i></p>
              </td>
              <td>Outcome identifier</td>
            </tr>
            <tr>
              <td><b>status*</b></td>
              <td>
                <p><i>enum</i></p>
                <p><b>required:</b>&nbsp;<i>true</i></p>
                <p><b>nullable:</b>&nbsp;<i>false</i></p>
              </td>
              <td>
                Possible values:
                <ul>
                  <li>resolved</li>
                  <li>declined</li>
                  <li>previously_resolved</li>
                  <li>notfound</li>
                </ul>
              </td>
            </tr>
            <tr>
              <td><b>refunded*</b></td>
              <td>
                <p><i>enum</i></p>
                <p><b>required:</b>&nbsp;<i>true</i></p>
                <p><b>nullable:</b>&nbsp;<i>false</i></p>
              </td>
              <td>
                Possible values:
                <ul>
                  <li>refunded</li>
                  <li>not refunded</li>
                  <li>not settled</li>
                </ul>
              </td>
            </tr>
            <tr>
              <td><b>createdAt*</b></td>
              <td>
                <p><i>string (ISO 8601)</i></p>
                <p><b>required:</b>&nbsp;<i>true</i></p>
                <p><b>nullable:</b>&nbsp;<i>false</i></p>
              </td>
              <td>When outcome was recorded</td>
            </tr>
          </tbody>
        </template>
      </v-simple-table>
    </v-card-text>

    <!-- 5. MIGRATION GUIDE -->
    <v-card-title id="migration-guide">6. Migration Guide (V1 to V2)</v-card-title>

    <v-card-title id="migration-overview" class="subtitle-1">6.1. Overview</v-card-title>
    <v-card-text>
      <p>This guide helps you migrate from Merchanto Events API V1 to V2. API V2 streamlines the integration process by simplifying status handling and reducing payload complexity. The key improvements include:</p>
      <ul class="mt-2">
        <li><b>Simplified Status Logic:</b> No need to select different statuses based on event or service type</li>
        <li><b>Reduced Fields:</b> Removed redundant and deprecated fields</li>
        <li><b>Unified Workflow:</b> Consistent status values across all event types</li>
      </ul>
      <p class="mt-2">You now only need to select from <b>4 event statuses</b> and <b>3 refund states</b> that accurately reflect your transaction state.</p>
    </v-card-text>

    <v-card-title id="field-changes" class="subtitle-1">6.2. Field Changes</v-card-title>
    <v-card-text>
      <p><b>Renamed Fields</b></p>
      <v-simple-table class="migration-fields-table my-2">
        <template #default>
          <thead>
            <tr>
              <th class="text-left">V1 Field Name</th>
              <th class="text-left">V2 Field Name</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td><code>alertId</code></td>
              <td><code>eventId</code></td>
            </tr>
            <tr>
              <td><code>alertTimestamp</code></td>
              <td><code>eventTimestamp</code></td>
            </tr>
          </tbody>
        </template>
      </v-simple-table>
      <p class="mt-2">Update all references in your code, database schemas, and API calls to use the new field names.</p>
      <p class="mt-2"><b>Removed Fields</b></p>
      <p class="mt-2">The following fields have been removed in V2:</p>
      <ul class="mt-2">
        <li><code>alertType</code></li>
        <li><code>memberId</code></li>
        <li><code>merchantCategoryCode</code></li>
        <li><code>merchantMemberName</code></li>
        <li><code>source</code></li>
        <li><code>websiteName</code></li>
        <li><code>outcome</code> (at root level - now nested when available)</li>
        <li><code>merchantName</code></li>
      </ul>
      <p class="mt-2"><b>Action Required:</b> Remove dependencies on these fields from your code, data models, and database schemas.</p>
    </v-card-text>

    <v-card-title id="status-simplification" class="subtitle-1">6.3. Status Simplification</v-card-title>
    <v-card-text>
      <p><b>Event Status Changes</b></p>
      <p class="mt-2">V1 had complex status selection logic based on <code>alertType</code> and <code>serviceType</code>. V2 uses universal status values:</p>
      <p class="mt-2"><b>Available Event Statuses:</b></p>
      <ul class="mt-2">
        <li><code>resolved</code> - Transaction refunded/canceled or issue resolved</li>
        <li><code>declined</code> - Will contest the dispute</li>
        <li><code>previously_resolved</code> - Already refunded/canceled before this event</li>
        <li><code>notfound</code> - Transaction not found in your system</li>
      </ul>
      <p class="mt-2"><b>Available Refund States:</b></p>
      <ul class="mt-2">
        <li><code>refunded</code> - Transaction was refunded</li>
        <li><code>not refunded</code> - Transaction was not refunded</li>
        <li><code>not settled</code> - Transaction was never settled (auth only)</li>
      </ul>
      <p class="mt-2">These statuses apply consistently regardless of card network or alert type.</p>
    </v-card-text>

    <v-card-title id="migration-steps" class="subtitle-1">6.4. Migration Steps</v-card-title>
    <v-card-text>
      <p>To successfully migrate your system from API version 1 to version 2, follow these detailed steps:</p>
      <p class="mt-2"><b>Step 1: Update Field Names</b></p>
      <p class="mt-2">Search your codebase for references to V1 field names:</p>
      <pre class="output"><code>
// Before (V1)
const alertId = event.alertId;
const timestamp = event.alertTimestamp;

// After (V2)
const eventId = event.eventId;
const timestamp = event.eventTimestamp;
        </code></pre>
      <p class="mt-2">Update database column names if you persist event data.</p>
      <p class="mt-2"><b>Step 2: Remove Deprecated Fields</b></p>
      <p class="mt-2">Identify and remove logic that depends on deprecated fields:</p>
      <ol class="mt-2">
        <li>Search for references to <code>alertType</code>, <code>memberId</code>, <code>merchantCategoryCode</code>, etc.</li>
        <li>Remove these fields from data models and DTOs</li>
        <li>Update database schemas to drop deprecated columns</li>
        <li>Remove any conditional logic based on these fields</li>
      </ol>
      <p class="mt-2"><b>Step 3: Simplify Status Handling</b></p>
      <p class="mt-2">Replace V1 status logic with simplified V2 statuses:</p>
      <pre class="output"><code>
// Before (V1) - Complex logic based on alert type
if (alertType === 'dispute_alert') {
  status = 'accepted';
} else if (alertType === 'fraud_alert') {
  status = 'stopped';
}

// After (V2) - Simple, universal statuses
if (transactionRefunded) {
  status = 'resolved';
  refunded = 'refunded';
} else if (transactionNotFound) {
  status = 'notfound';
  refunded = 'not refunded';
}
        </code></pre>
      <p class="mt-2">Map your existing status values to the 4 simplified V2 statuses based on the actual transaction outcome.</p>
      <p class="mt-2"><b>Step 4: Test in Sandbox</b></p>
      <p class="mt-2">Before migrating production:</p>
      <ol class="mt-2">
        <li>Update your test environment endpoints</li>
        <li>Configure V2 credentials for the sandbox environment</li>
        <li>Process test events and verify correct status updates</li>
        <li>Validate webhook receipt and response handling</li>
        <li>Test event retrieval via GET endpoints</li>
      </ol>
      <p class="mt-2"><b>Step 5: Coordinate Production Migration</b></p>
      <p class="mt-2"><b>Important:</b> V2 must be enabled manually by Merchanto.</p>
      <ol class="mt-2">
        <li>Notify your platform representative when ready to migrate</li>
        <li>Schedule a migration window</li>
        <li>Merchanto will enable V2 for your production account</li>
        <li>Monitor initial events closely after migration</li>
        <li>Contact support immediately if issues arise</li>
      </ol>
      <p class="mt-2"><b>Support</b></p>
      <ul class="mt-2">
        <li><b>Migration Assistance:</b> Contact your platform representative to schedule a migration consultation</li>
        <li><b>Testing Sessions:</b> Request guided testing sessions in the sandbox environment</li>
        <li><b>Timeline:</b> Plan your migration soon - V1 support will be discontinued</li>
      </ul>
      <p class="mt-2">We're available to help ensure a smooth transition to V2.</p>
    </v-card-text>
  </div>
</template>

<script>
import useDocs from '@/views/docs/useDocs';

export default {
  name: 'MerchantoEventsDocumentationV2',
  setup() {
    const { scrollToElement } = useDocs();

    return { scrollToElement };
  }
};
</script>

<style scoped>
.merchanto-api-v2-scheme {
  max-width: 100%;
  height: auto;
}

.output {
  background-color: #f5f5f5;
  padding: 12px;
  border-radius: 4px;
  overflow-x: auto;
}

.output code {
  font-family: 'Courier New', monospace;
  font-size: 13px;
  line-height: 1.5;
}

.code {
  font-family: 'Courier New', monospace;
  color: #d63384;
}
</style>
